import React, { useState, useEffect, useRef, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import apiClient from './services/api';
import { cacheUser, readCachedUser, clearSession, isSessionInvalidError } from './services/session';
import { isDosisEnabled } from './services/dosis'; // ola 3 · O3.6 / M2: interruptor encendido por defecto
import { toast, Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';

// Context & Components (eager imports - used across routes)
import ErrorBoundary from './components/ErrorBoundary';
import DOMErrorBoundary from './components/DOMErrorBoundary';
import PageLoadingSkeleton from './components/PageLoadingSkeleton';
import { BrandingProvider } from './contexts/BrandingContext';
import MobileAppWrapper from './components/mobile/MobileAppWrapper';
import TutorChat from './components/TutorChat';
import OnboardingGate from './components/onboarding/OnboardingGate';

// Page imports - lazy loaded for code splitting
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const PlanSelector = React.lazy(() => import('./pages/PlanSelector'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const ExamPage = React.lazy(() => import('./pages/ExamPage'));
const ResultsPage = React.lazy(() => import('./pages/ResultsPage'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const AdminLoginPage = React.lazy(() => import('./pages/AdminLoginPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const CreditsPage = React.lazy(() => import('./pages/CreditsPage'));
const PracticeFailedPage = React.lazy(() => import('./pages/PracticeFailedPage'));
const FlashcardsPage = React.lazy(() => import('./pages/FlashcardsPage'));
const StudyPlanPage = React.lazy(() => import('./pages/StudyPlanPage'));
const OfflineTestPage = React.lazy(() => import('./pages/OfflineTestPage'));
const LibraryPage = React.lazy(() => import('./pages/LibraryPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const LogoDownloadPage = React.lazy(() => import('./pages/LogoDownloadPage'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const CompliancePage = React.lazy(() => import('./pages/CompliancePage'));

// Producto mono-oposición Baleares (SUAP/061)
const ConvocatoriaPage = React.lazy(() => import('./pages/ConvocatoriaPage'));
const MiMaterialPage = React.lazy(() => import('./pages/MiMaterialPage'));
const MisExplicacionesPage = React.lazy(() => import('./pages/MisExplicacionesPage'));
const BibliotecaRepasoPage = React.lazy(() => import('./pages/BibliotecaRepasoPage'));
const PrimerDiaPage = React.lazy(() => import('./pages/PrimerDiaPage')); // Ola 1 · W2d: primer día guiado
const DosisPage = React.lazy(() => import('./pages/DosisPage')); // Ola 3 · O3.6: cálculo de dosis guiado (REACT_APP_DOSIS_ENABLED, encendido salvo 'false')

// NOTA mono-oposición: los módulos B2B/CRM/ERP/SSO/Moodle/KDP/Torneos/Academias/
// whitelabel/Mentorship/Community están fuera de alcance y NO se registran en el
// router (los ficheros de página se conservan en src/pages/).

// Premium Pages
const GruposEstudioPage = React.lazy(() => import('./pages/GruposEstudioPage'));
const ProfesorDashboard = React.lazy(() => import('./pages/ProfesorDashboard'));
const RetosLogrosPage = React.lazy(() => import('./pages/RetosLogrosPage'));

// AI Premium Pages
const TutorIAPage = React.lazy(() => import('./pages/TutorIAPage'));
const ResumenesIAPage = React.lazy(() => import('./pages/ResumenesIAPage'));
const KPIsPremiumPage = React.lazy(() => import('./pages/KPIsPremiumPage'));
const ProgresoPage = React.lazy(() => import('./pages/ProgresoPage')); // ola 2 · O2.4: tú frente a ti hace 30 días

// New Pages
const MapasMentalesPage = React.lazy(() => import('./pages/MapasMentalesPage'));
const MultimediaContentPage = React.lazy(() => import('./pages/MultimediaContentPage'));
const ContentStudioPage = React.lazy(() => import('./pages/ContentStudioPage'));

// Suppress known harmless errors from MetaMask/ethereum browser extensions
// Only suppress if the error originates from the window.ethereum provider
const KNOWN_EXTENSION_PATTERNS = [
  'MetaMask',
  'window.ethereum',
  'ethereum#initialized',
  'EIP-6963',
  'already pending for origin',
];

const isKnownExtensionError = (msg, source) => {
  const hasExtensionKeyword = KNOWN_EXTENSION_PATTERNS.some(p => msg.includes(p));
  const fromExtensionFile = source?.includes('inpage') || source?.includes('contentscript');
  return hasExtensionKeyword || (msg.includes('ethereum') && fromExtensionFile);
};

window.addEventListener('error', (e) => {
  const msg = e.message || '';
  if (isKnownExtensionError(msg, e.filename)) {
    // eslint-disable-next-line no-console -- traza de depuración de errores de extensiones suprimidos
    console.debug('[Extension] Suppressed known browser extension error:', msg);
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
  // Log unexpected errors that look extension-related but aren't in the known list
  if (msg.includes('ethereum') || msg.includes('MetaMask') || e.filename?.includes('inpage') || e.filename?.includes('contentscript')) {
    console.warn('Suppressed browser extension error:', e.message);
  }
});

window.addEventListener('unhandledrejection', (e) => {
  const msg = e.reason?.message || '';
  const stack = e.reason?.stack || '';
  if (isKnownExtensionError(msg, stack)) {
    // eslint-disable-next-line no-console -- traza de depuración de errores de extensiones suprimidos
    console.debug('[Extension] Suppressed known browser extension rejection:', msg);
    e.preventDefault();
    return false;
  }
  // Log unexpected extension-like errors that were not in the known list
  if (msg.includes('ethereum') || msg.includes('MetaMask') || stack.includes('inpage') || stack.includes('contentscript')) {
    console.warn('Suppressed browser extension error:', e.reason?.message);
  }
});

import { API, API_BASE_URL } from './config/api';
import { startApiFailoverWatch } from './config/apiFailover';

const BACKEND_URL = API_BASE_URL;

axios.defaults.baseURL = BACKEND_URL;
axios.defaults.withCredentials = true;
// Si el host de la API empaquetado no responde pero su dominio de Railway sí, cambia de host
// (config/apiFailover.js). En condiciones normales no hace nada visible.
startApiFailoverWatch();

// Producto mono-oposición: España (Illes Balears) + Enfermería de Urgencias de AP.
// El registro no pide país ni profesión: se fijan automáticamente (contrato C1).
const DEFAULT_COUNTRY = 'ES';
const DEFAULT_PROFESSION_ID = 'prof_enf_urgencias_ap';
const DEFAULT_REGION = 'baleares';
const DEFAULT_SCOPE = 'autonomico';

// Sustituye a los antiguos selectores de país/profesión: configura la oposición
// por defecto en el backend y redirige al plan o al dashboard.
const AutoProfessionSetup = () => {
  const navigate = useNavigate();
  const hasRun = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const setup = async () => {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        await axios.post(
          `${API}/user/select-profession`,
          { profession_id: DEFAULT_PROFESSION_ID, region: DEFAULT_REGION, scope: DEFAULT_SCOPE },
          { headers }
        );

        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        currentUser.selected_country = DEFAULT_COUNTRY;
        currentUser.selected_profession = DEFAULT_PROFESSION_ID;
        currentUser.selected_region = DEFAULT_REGION;
        currentUser.selected_scope = DEFAULT_SCOPE;
        localStorage.setItem('user', JSON.stringify(currentUser));

        try {
          const accessRes = await axios.get(`${API}/system/check-access`, { headers });
          if (accessRes.data.require_plan_selection && !accessRes.data.subscription_tier) {
            navigate('/select-plan', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } catch {
          navigate('/dashboard', { replace: true });
        }
      } catch (_error) {
        setFailed(true);
      }
    };
    setup();
  }, [navigate]);

  if (failed) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-slate-200 max-w-md">
          <p className="text-slate-900 font-bold mb-2">No hemos podido configurar tu oposición</p>
          <p className="text-slate-600 text-sm mb-4">
            Comprueba tu conexión e inténtalo de nuevo.
          </p>
          <button
            onClick={() => { hasRun.current = false; setFailed(false); window.location.reload(); }}
            className="px-6 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-slate-600">Configurando tu oposición de Enfermería de Urgencias (IB-Salut)…</p>
      </div>
    </div>
  );
};

// LegacyAuthCallback component for handling OAuth legacy (session_id)
const LegacyAuthCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async (attempt = 0) => {
      // Extract session_id from URL fragment
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error(t('auth.sessionNotFound'));
        navigate('/auth');
        return;
      }

      const sessionId = sessionIdMatch[1];
      
      try {
        setStatus('processing');
        // Call backend to process session with timeout
        const response = await apiClient.get(`${API}/auth/session?session_id=${sessionId}`, {
          timeout: 15000 // 15 second timeout
        });
        
        // Store tokens
        localStorage.setItem('session_token', response.data.session_token);
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        
        setStatus('success');
        toast.success(t('auth.welcome'));
        
        // Check if user needs to select profession
        if (!response.data.user.selected_profession) {
          navigate('/select-profession', { state: { user: response.data.user } });
        } else {
          navigate('/dashboard', { state: { user: response.data.user } });
        }
      } catch (error) {
        // Retry logic - up to 2 retries
        if (attempt < 2) {
          setRetryCount(attempt + 1);
          setStatus('retrying');
          // Wait 1 second before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
          return processSession(attempt + 1);
        }
        
        // Get specific error message
        let errorMsg = t('auth.processingError');
        if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.code === 'ECONNABORTED') {
          errorMsg = t('auth.timeoutError');
        } else if (error.message?.includes('Network Error')) {
          errorMsg = t('auth.networkError');
        }
        
        setStatus('error');
        toast.error(errorMsg);
        
        // Wait 2 seconds before redirecting so user can see the error
        setTimeout(() => navigate('/auth'), 2000);
      }
    };

    processSession();
  }, [navigate, location, t]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-slate-200 max-w-md">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-slate-600">{t('auth.processing')}</p>
          </>
        )}
        {status === 'retrying' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
            <p className="text-slate-600">{t('auth.retrying', { count: retryCount })}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-600 font-medium">{t('auth.success')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 font-medium">{t('auth.error')}</p>
            <p className="text-slate-500 text-sm mt-2">{t('auth.redirecting')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [offline, setOffline] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      cacheUser(location.state.user);
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) {
          setIsAuthenticated(false);
          return;
        }

        const response = await apiClient.get(`${API}/auth/me`);
        cacheUser(response.data);
        setUser(response.data);
        setOffline(false);
        setIsAuthenticated(true);
      } catch (error) {
        // Solo un 401/403 real invalida la sesión. Sin red, timeout o 5xx se
        // conservan los tokens y se entra con el usuario cacheado (P12.4).
        if (isSessionInvalidError(error)) {
          clearSession();
          setIsAuthenticated(false);
          return;
        }
        const cached = readCachedUser();
        console.warn('auth_network_fallback', { status: error?.response?.status ?? 'network', cached: !!cached });
        if (cached) {
          setUser(cached);
          setIsAuthenticated(true);
          return;
        }
        setOffline(true);
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, [location, navigate, attempt]);

  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAuthenticated && offline) {
    // Hay token pero no usuario cacheado: no se manda a /auth (allí el login
    // tampoco funcionaría sin red); se conserva la sesión y se ofrece reintentar.
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-6 text-center" role="status" aria-live="polite" data-testid="auth-offline">
        <p className="text-lg font-semibold text-slate-900">{t('auth.offlineTitle', 'Sin conexión con el servidor')}</p>
        <p className="text-sm text-slate-600">{t('auth.offlineHelp', 'Tu sesión se conserva. Comprueba la conexión y vuelve a intentarlo.')}</p>
        <button
          type="button"
          onClick={() => { setIsAuthenticated(null); setAttempt((n) => n + 1); }}
          className="px-5 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
        >
          {t('common.retry', 'Reintentar')}
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Conservar el destino (p. ej. /results/exam_XXXX al recargar) para volver
    // exactamente ahí tras iniciar sesión, en vez de acabar en /dashboard.
    const next = location.pathname + location.search;
    const nextParam = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
    return <Navigate to={`/auth${nextParam}`} replace />;
  }

  return React.cloneElement(children, { user });
};

// AdminRoute - Only for super admins
const AdminRoute = ({ children }) => {
  const [authState, setAuthState] = useState({ loading: true, isAdmin: false, user: null });
  const _navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) {
          setAuthState({ loading: false, isAdmin: false, user: null });
          return;
        }

        // First get user info
        const userResponse = await apiClient.get(`${API}/auth/me`);

        // Then check if admin
        const adminResponse = await apiClient.get(`${API}/auth/check-admin`);

        setAuthState({ 
          loading: false, 
          isAdmin: adminResponse.data.is_admin, 
          user: userResponse.data 
        });
      } catch (_error) {
        setAuthState({ loading: false, isAdmin: false, user: null });
      }
    };
    checkAdmin();
  }, [location]);

  if (authState.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!authState.isAdmin) {
    return <Navigate to="/admin-login" replace />;
  }

  return React.cloneElement(children, { user: authState.user });
};

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Normalize double slashes in URL path (e.g. //auth/callback -> /auth/callback)
  useEffect(() => {
    if (location.pathname.includes('//')) {
      const normalized = location.pathname.replace(/\/\/+/g, '/');
      navigate(normalized + location.search + location.hash, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  // Check URL fragment for session_id (OAuth legacy (session_id) callback)
  // This MUST be checked synchronously during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <LegacyAuthCallback />;
  }

  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoadingSkeleton />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Mono-oposición: sin selector de país ni de profesión */}
      <Route path="/select-country" element={<Navigate to="/select-profession" replace />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route
        path="/select-profession"
        element={
          <ProtectedRoute>
            <AutoProfessionSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/select-plan"
        element={
          <ProtectedRoute>
            <PlanSelector />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {/* Ola 1 · W2d: solo la primera vez tras iniciar sesión, si el primer día
                guiado está pendiente (ni completado ni descartado), redirige a /primer-dia. */}
            <OnboardingGate>
              <Dashboard />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/primer-dia"
        element={
          <ProtectedRoute>
            <PrimerDiaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam"
        element={
          <ProtectedRoute>
            <ExamPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/results/:examId"
        element={
          <ProtectedRoute>
            <ResultsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin-login" element={<AdminLoginPage />} />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/credits"
        element={
          <ProtectedRoute>
            <CreditsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/practice-failed"
        element={
          <ProtectedRoute>
            <PracticeFailedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/convocatoria"
        element={
          <ProtectedRoute>
            <ConvocatoriaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mi-material"
        element={
          <ProtectedRoute>
            <MiMaterialPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mis-explicaciones"
        element={
          <ProtectedRoute>
            <MisExplicacionesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/biblioteca"
        element={
          <ProtectedRoute>
            <BibliotecaRepasoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/flashcards"
        element={
          <ProtectedRoute>
            <FlashcardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/study-plan"
        element={
          <ProtectedRoute>
            <StudyPlanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offline-test"
        element={
          <ProtectedRoute>
            <OfflineTestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        }
      />
      <Route path="/logos" element={<LogoDownloadPage />} />
      <Route path="/aviso-legal" element={<LegalPage />} />
      <Route path="/privacidad" element={<PrivacyPage />} />
      <Route path="/contacto" element={<ContactPage />} />
      {/* Módulos B2B/institucionales desregistrados (producto mono-oposición) */}
      <Route path="/compliance" element={<CompliancePage />} />
      <Route path="/gdpr" element={<CompliancePage />} />
      {/* Premium Feature Routes */}
      <Route 
        path="/grupos" 
        element={
          <ProtectedRoute>
            <GruposEstudioPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/profesor"
        element={
          <ProtectedRoute>
            <ProfesorDashboard />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/retos"
        element={
          <ProtectedRoute>
            <RetosLogrosPage />
          </ProtectedRoute>
        } 
      />
      {/* AI Premium Pages */}
      <Route 
        path="/tutor-ia" 
        element={
          <ProtectedRoute>
            <TutorIAPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/resumenes-ia" 
        element={
          <ProtectedRoute>
            <ResumenesIAPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/kpis"
        element={
          <ProtectedRoute>
            <KPIsPremiumPage />
          </ProtectedRoute>
        }
      />
      <Route path="/progreso" element={<ProtectedRoute><ProgresoPage /></ProtectedRoute>} />
      {/* ola 3 · O3.6: la ruta existe salvo que el flag de build la apague (la página comprueba además el del servidor) */}
      {isDosisEnabled() && (
        <Route path="/dosis" element={<ProtectedRoute><DosisPage /></ProtectedRoute>} />
      )}
      {/* New Feature Pages */}
      <Route
        path="/mapas-mentales"
        element={
          <ProtectedRoute>
            <MapasMentalesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/multimedia" 
        element={
          <ProtectedRoute>
            <MultimediaContentPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/content-studio" 
        element={
          <ProtectedRoute>
            <ContentStudioPage />
          </ProtectedRoute>
        } 
      />
    </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <DOMErrorBoundary>
      <div className="App">
        <BrandingProvider>
          <BrowserRouter>
            <MobileAppWrapper>
              <AppRouter />
              <TutorChat />
            </MobileAppWrapper>
          </BrowserRouter>
        </BrandingProvider>
        <Toaster position="top-right" richColors />
      </div>
    </DOMErrorBoundary>
    </ErrorBoundary>
  );
}

export default App;
